/**
 * Sprint V102.j (So 27.09.2026) — Admin-Marketing-Panel
 * ---------------------------------------------------------------------------
 * Verwaltung von Marketing-Bannern (Trial-Push u.a.) und In-App-Kampagnen.
 * Beide Konzepte sind Kampagnen-getriebene Kommunikationskanaele — Banner
 * = passiv oberhalb der Fold, Modal = aktiv beim naechsten Login.
 *
 * Auth: Admin-Cookie (Bearer-Rolle=admin) via api-Instance.
 * Mount: /admin/marketing (siehe App.js).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Save, Trash2, RefreshCcw, Sparkles, Megaphone, Mail, Send } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";

const BG_COLORS = ["amber", "blue", "orange", "emerald", "slate"];
const TIER_OPTIONS = [
  { value: "all", label: "Alle User" },
  { value: "free", label: "Nur Free (inkl. Pioniere)" },
  { value: "pro", label: "Nur Pro" },
];

const BLANK_BANNER = {
  key: "",
  enabled: true,
  headline: "",
  subline: "",
  cta_label: "",
  cta_url: "",
  bg_color: "amber",
  target_tier: "free",
  dismissible: true,
};

const BLANK_CAMPAIGN = {
  key: "",
  enabled: true,
  headline: "",
  body: "",
  cta_primary_label: "",
  cta_primary_url: "",
  cta_secondary_label: "Vielleicht später",
  target_tier: "free",
  starts_at: "",
  ends_at: "",
};

export default function AdminMarketing() {
  const [tab, setTab] = useState("banners"); // banners | campaigns
  const [banners, setBanners] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get("/admin/marketing/banners"),
        api.get("/admin/marketing/campaigns"),
      ]);
      setBanners(b.data?.banners || []);
      setCampaigns(c.data?.campaigns || []);
    } catch (e) {
      setFlash({ type: "error", text: "Konnte Daten nicht laden. Admin-Rechte ok?" });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showFlash = (type, text) => {
    setFlash({ type, text });
    setTimeout(() => setFlash(null), 3500);
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-marketing-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              Marketing
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Banner & In-App-Kampagnen fuer Trial-Push und weitere Aktionen.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-marketing-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
            Aktualisieren
          </button>
        </div>

        {flash ? (
          <div
            data-testid="admin-marketing-flash"
            className={`mb-4 rounded-lg px-4 py-2 text-sm ${
              flash.type === "error"
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
            }`}
          >
            {flash.text}
          </div>
        ) : null}

        <div className="flex gap-2 border-b border-slate-200 mb-6">
          <button
            type="button"
            data-testid="admin-marketing-tab-banners"
            onClick={() => setTab("banners")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "banners"
                ? "border-amber-500 text-amber-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Megaphone className="w-4 h-4 inline mr-1.5" />
            Banner
          </button>
          <button
            type="button"
            data-testid="admin-marketing-tab-campaigns"
            onClick={() => setTab("campaigns")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "campaigns"
                ? "border-amber-500 text-amber-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-1.5" />
            In-App-Kampagnen
          </button>
          <button
            type="button"
            data-testid="admin-marketing-tab-push"
            onClick={() => setTab("push")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "push"
                ? "border-amber-500 text-amber-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Mail className="w-4 h-4 inline mr-1.5" />
            Mail-Push
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : tab === "banners" ? (
          <BannersTab items={banners} reload={load} flash={showFlash} />
        ) : tab === "campaigns" ? (
          <CampaignsTab items={campaigns} reload={load} flash={showFlash} />
        ) : (
          <PushTab flash={showFlash} />
        )}
      </div>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Push-Tab — Trial-Push Mail-Blast
// ---------------------------------------------------------------------------
function PushTab({ flash }) {
  const [busy, setBusy] = useState(false);
  const [lastStats, setLastStats] = useState(null);
  const [ktList, setKtList] = useState("");
  const [ktStats, setKtStats] = useState(null);
  const [testList, setTestList] = useState("");
  const [testStats, setTestStats] = useState(null);

  const run = async (dryRun) => {
    if (!dryRun) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        "Wirklich echte Mails an ALLE aktiven Free-User (inkl. Pioniere) senden? Dies laesst sich nicht rueckgaengig machen.",
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/admin/marketing/trial-push/send?dry_run=${dryRun}`, null, { timeout: 30000 });
      setLastStats(r.data);
      if (dryRun) {
        flash("success", "Dry-Run abgeschlossen.");
      } else if (r.data?.queued) {
        flash(
          "success",
          "Push gestartet — läuft im Hintergrund. Klick jetzt Dry-Run, um den Fortschritt zu sehen (skipped_already_pushed steigt).",
        );
      } else {
        flash("success", `Push gesendet: ${r.data?.sent || 0} Mails.`);
      }
    } catch (e) {
      flash(
        "error",
        "Push-Auftrag konnte nicht übermittelt werden. Klick trotzdem Dry-Run — falls der Send bereits gestartet ist, siehst du das an skipped_already_pushed.",
      );
    }
    setBusy(false);
  };

  const seedPreset = async () => {
    setBusy(true);
    try {
      await api.post("/admin/marketing/seed-v102j");
      flash("success", "V102.j-Preset angelegt: Banner + Modal sind ab sofort für Free-User sichtbar.");
    } catch {
      flash("error", "Preset-Anlage fehlgeschlagen.");
    }
    setBusy(false);
  };

  const runKtSync = async (dryRun) => {
    const emails = ktList
      .split(/[\s,;\n]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes("@"));
    if (emails.length === 0) {
      flash("error", "Bitte E-Mail-Liste einfügen (eine pro Zeile oder mit Komma getrennt).");
      return;
    }
    if (!dryRun) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Wirklich alle User, die die V102.j-Mail bekommen haben und NICHT in der Liste (${emails.length} Adressen) sind, als KT-abgemeldet markieren?`,
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/marketing/kt-optout-sync", {
        confirmed_emails: emails,
        dry_run: dryRun,
      });
      setKtStats(r.data);
      flash("success", dryRun ? "Dry-Run KT-Sync abgeschlossen." : `KT-Sync angewendet: ${r.data?.applied || 0} User markiert.`);
    } catch (e) {
      flash("error", "KT-Sync fehlgeschlagen.");
    }
    setBusy(false);
  };

  const markTestAccounts = async () => {
    const emails = testList
      .split(/[\s,;\n]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes("@"));
    if (emails.length === 0) {
      flash("error", "Bitte E-Mail-Liste einfügen.");
      return;
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `${emails.length} User als Testkonto markieren? Sie werden aus allen Marketing-, Nurture- und Reminder-Mails permanent ausgeschlossen.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/marketing/mark-test-accounts", { emails });
      setTestStats(r.data);
      flash("success", `${r.data?.marked || 0} von ${r.data?.requested || 0} User als Testkonto markiert.`);
    } catch {
      flash("error", "Markierung fehlgeschlagen.");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <section className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h2 className="text-lg font-bold text-slate-900">Ein-Klick-Setup (V102.j)</h2>
        <p className="mt-1 text-sm text-slate-700 leading-relaxed">
          Legt den <strong>Trial-Push-Banner</strong> und die <strong>In-App-Kampagne</strong>
          in einem Rutsch an (mit dem korrekten V102.j-Wording). Nach dem Klick sind Banner
          und Modal <strong>sofort</strong> auf <code>/pricing</code>, <code>/cockpit</code>
          und im Dashboard fuer Free-User sichtbar. Idempotent — mehrfaches Klicken schadet nicht.
        </p>
        <div className="mt-4">
          <button
            type="button"
            data-testid="admin-marketing-seed-v102j"
            onClick={seedPreset}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Banner + Modal jetzt anlegen (V102.j-Preset)
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-lg font-bold text-slate-900">Trial-Push (V102.j)</h2>
        <p className="mt-1 text-sm text-slate-600 leading-relaxed">
          Einmalige Mail-Kampagne an <strong>alle aktiven Free-User (inkl. Pioniere)</strong>.
          Bewirbt die 7-Tage-Trial-Option fuer 1 €. Filter: nur DOI-bestaetigt, keine Testkonten,
          nicht bereits im Trial, noch nie gepusht (idempotent). Ohne Reminder-Storm — Single Push.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          <strong>Empfehlung:</strong> Zuerst Dry-Run laufen lassen, dann echten Send starten.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            data-testid="admin-marketing-push-dry-run"
            onClick={() => run(true)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Dry-Run (kein Send)
          </button>
          <button
            type="button"
            data-testid="admin-marketing-push-real"
            onClick={() => run(false)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Jetzt senden (echte Mails)
          </button>
        </div>
      </section>

      {lastStats ? (
        <section className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="admin-marketing-push-stats">
          <h3 className="text-sm font-bold text-slate-900 mb-2">Letzter Lauf</h3>
          {lastStats.skipped_non_prod_domain > 0 ? (
            <div
              data-testid="admin-marketing-push-warning"
              className="mb-3 rounded-lg border-l-4 border-red-500 bg-red-50 text-red-900 px-4 py-3 text-sm"
            >
              <strong>Warnung:</strong> {lastStats.skipped_non_prod_domain} User wurden vom
              Non-Prod-Guard geblockt. Auf Prod muss dieser Wert <strong>0</strong> sein.
              Wenn du hier auf Prod stehst und die Zahl &gt; 0 ist, ist APP_ENV nicht auf
              &quot;production&quot; und der Domain-Fallback greift nicht. Nicht senden!
            </div>
          ) : null}
          <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap">{JSON.stringify(lastStats, null, 2)}</pre>
        </section>
      ) : null}

      <section className="bg-white border border-amber-300 rounded-xl p-5">
        <h2 className="text-lg font-bold text-slate-900">KT-Opt-out-Abgleich (V102.j.2)</h2>
        <p className="mt-1 text-sm text-slate-700 leading-relaxed">
          Paste die aktuelle <strong>KlickTipp-Confirmed-Liste</strong> (nur E-Mail-Adressen, eine pro Zeile
          oder komma-getrennt). Der Sync findet alle User, die die V102.j-Mail bekommen haben und
          <strong> nicht</strong> in dieser Liste sind — sie werden dann als
          <code> kt_unsubscribed_at</code> + <code>marketing_unsubscribed_at</code> markiert.
          Damit sind sie von <strong>allen</strong> künftigen Marketing-Mails ausgeschlossen.
        </p>
        <textarea
          data-testid="kt-sync-list"
          rows={6}
          className="mt-3 w-full border border-slate-300 rounded-md px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none"
          placeholder="a@b.de&#10;c@d.com&#10;..."
          value={ktList}
          onChange={(e) => setKtList(e.target.value)}
        />
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            data-testid="kt-sync-dry-run"
            onClick={() => runKtSync(true)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Dry-Run (Vorschau)
          </button>
          <button
            type="button"
            data-testid="kt-sync-apply"
            onClick={() => runKtSync(false)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Anwenden
          </button>
        </div>
        {ktStats ? (
          <pre data-testid="kt-sync-stats" className="mt-4 text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap">
            {JSON.stringify(ktStats, null, 2)}
          </pre>
        ) : null}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-lg font-bold text-slate-900">Test-Accounts markieren (Bulk)</h2>
        <p className="mt-1 text-sm text-slate-700 leading-relaxed">
          Trage hier E-Mail-Adressen ein, die als <strong>Testkonten</strong> markiert werden sollen
          (Smoke-Tests, Debug-Accounts, interne Konten). Setzt <code>is_test_account=true</code>
          plus alle 5 Opt-out-Flags — die User bekommen <strong>niemals mehr</strong> Marketing-,
          Nurture-, Video-Drip- oder Reminder-Mails.
        </p>
        <textarea
          data-testid="test-account-list"
          rows={5}
          className="mt-3 w-full border border-slate-300 rounded-md px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none"
          placeholder="test@example.de&#10;smoke@..."
          value={testList}
          onChange={(e) => setTestList(e.target.value)}
        />
        <div className="mt-3">
          <button
            type="button"
            data-testid="test-account-apply"
            onClick={markTestAccounts}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Als Testkonto markieren
          </button>
        </div>
        {testStats ? (
          <pre data-testid="test-account-stats" className="mt-4 text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap">
            {JSON.stringify(testStats, null, 2)}
          </pre>
        ) : null}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banners-Tab
// ---------------------------------------------------------------------------
function BannersTab({ items, reload, flash }) {
  const [draft, setDraft] = useState(BLANK_BANNER);
  const [saving, setSaving] = useState(false);

  const upsert = async () => {
    if (!draft.key.trim() || !draft.headline.trim() || !draft.cta_label.trim() || !draft.cta_url.trim()) {
      flash("error", "Bitte Pflichtfelder (Key, Headline, CTA-Label, CTA-URL) ausfuellen.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/marketing/banners", draft);
      flash("success", `Banner '${draft.key}' gespeichert.`);
      setDraft(BLANK_BANNER);
      reload();
    } catch (e) {
      flash("error", "Speichern fehlgeschlagen.");
    }
    setSaving(false);
  };

  const remove = async (key) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Banner '${key}' wirklich loeschen?`)) return;
    try {
      await api.delete(`/admin/marketing/banners/${encodeURIComponent(key)}`);
      flash("success", "Banner geloescht.");
      reload();
    } catch {
      flash("error", "Loeschen fehlgeschlagen.");
    }
  };

  const resetDismisses = async (key) => {
    try {
      const r = await api.post(`/admin/marketing/banners/${encodeURIComponent(key)}/reset-dismisses`);
      flash("success", `Dismisses zurueckgesetzt (${r.data?.reset_count} User).`);
    } catch {
      flash("error", "Reset fehlgeschlagen.");
    }
  };

  const editExisting = (item) => {
    setDraft({ ...BLANK_BANNER, ...item });
  };

  return (
    <div className="space-y-8">
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-lg font-bold text-slate-900 mb-4">
          {draft.key && items.find((i) => i.key === draft.key) ? "Bearbeiten" : "Neuen Banner anlegen"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Key (Slug, eindeutig)" value={draft.key} onChange={(v) => setDraft({ ...draft, key: v })} testid="banner-form-key" placeholder="v102j_trial_push" />
          <SelectField label="Ziel-Tier" value={draft.target_tier} onChange={(v) => setDraft({ ...draft, target_tier: v })} options={TIER_OPTIONS} testid="banner-form-tier" />
          <TextField label="Headline" value={draft.headline} onChange={(v) => setDraft({ ...draft, headline: v })} testid="banner-form-headline" full />
          <TextField label="Subline" value={draft.subline} onChange={(v) => setDraft({ ...draft, subline: v })} testid="banner-form-subline" full />
          <TextField label="CTA-Label" value={draft.cta_label} onChange={(v) => setDraft({ ...draft, cta_label: v })} testid="banner-form-cta-label" />
          <TextField label="CTA-URL" value={draft.cta_url} onChange={(v) => setDraft({ ...draft, cta_url: v })} testid="banner-form-cta-url" placeholder="/pricing" />
          <SelectField label="Farbe" value={draft.bg_color} onChange={(v) => setDraft({ ...draft, bg_color: v })} options={BG_COLORS.map((c) => ({ value: c, label: c }))} testid="banner-form-color" />
          <div className="flex items-center gap-6 pt-6">
            <ToggleField label="Aktiv" value={draft.enabled} onChange={(v) => setDraft({ ...draft, enabled: v })} testid="banner-form-enabled" />
            <ToggleField label="Wegklickbar" value={draft.dismissible} onChange={(v) => setDraft({ ...draft, dismissible: v })} testid="banner-form-dismissible" />
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            data-testid="banner-form-save"
            onClick={upsert}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </button>
          <button
            type="button"
            onClick={() => setDraft(BLANK_BANNER)}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
          >
            Zuruecksetzen
          </button>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-bold text-slate-900 mb-3">Vorhandene Banner ({items.length})</h2>
        <div className="space-y-3">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Keine Banner angelegt.</p>
          ) : (
            items.map((b) => (
              <div key={b.key} data-testid={`banner-row-${b.key}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{b.key}</code>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${b.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                      {b.enabled ? "aktiv" : "inaktiv"}
                    </span>
                    <span className="text-xs text-slate-500">{b.target_tier}</span>
                    <span className="text-xs text-slate-400">· {b.bg_color}</span>
                  </div>
                  <p className="mt-1.5 font-semibold text-sm text-slate-900">{b.headline}</p>
                  {b.subline ? <p className="text-xs text-slate-600 mt-0.5">{b.subline}</p> : null}
                  <p className="text-xs text-slate-500 mt-1">CTA: <span className="font-medium">{b.cta_label}</span> → <code>{b.cta_url}</code></p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button type="button" onClick={() => editExisting(b)} className="text-xs text-blue-700 hover:underline font-semibold">Bearbeiten</button>
                  <button type="button" onClick={() => resetDismisses(b.key)} className="text-xs text-slate-600 hover:underline">Dismisses reset</button>
                  <button type="button" onClick={() => remove(b.key)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Loeschen</button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaigns-Tab
// ---------------------------------------------------------------------------
function CampaignsTab({ items, reload, flash }) {
  const [draft, setDraft] = useState(BLANK_CAMPAIGN);
  const [saving, setSaving] = useState(false);

  const upsert = async () => {
    if (!draft.key.trim() || !draft.headline.trim() || !draft.body.trim() || !draft.cta_primary_label.trim() || !draft.cta_primary_url.trim()) {
      flash("error", "Bitte Pflichtfelder ausfuellen.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...draft };
      // ISO-Normalisierung; datetime-local -> ISO mit +02:00 (Europe/Berlin approx)
      if (payload.starts_at && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(payload.starts_at)) payload.starts_at = `${payload.starts_at}:00+02:00`;
      if (payload.ends_at && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(payload.ends_at)) payload.ends_at = `${payload.ends_at}:00+02:00`;
      await api.post("/admin/marketing/campaigns", payload);
      flash("success", `Kampagne '${draft.key}' gespeichert.`);
      setDraft(BLANK_CAMPAIGN);
      reload();
    } catch (e) {
      flash("error", "Speichern fehlgeschlagen.");
    }
    setSaving(false);
  };

  const remove = async (key) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Kampagne '${key}' wirklich loeschen?`)) return;
    try {
      await api.delete(`/admin/marketing/campaigns/${encodeURIComponent(key)}`);
      flash("success", "Kampagne geloescht.");
      reload();
    } catch {
      flash("error", "Loeschen fehlgeschlagen.");
    }
  };

  const resetDismisses = async (key) => {
    try {
      const r = await api.post(`/admin/marketing/campaigns/${encodeURIComponent(key)}/reset-dismisses`);
      flash("success", `Dismisses zurueckgesetzt (${r.data?.reset_count} User).`);
    } catch {
      flash("error", "Reset fehlgeschlagen.");
    }
  };

  const editExisting = (item) => {
    const clean = { ...BLANK_CAMPAIGN, ...item };
    // ISO -> datetime-local
    const shortify = (s) => (s && s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/) ? s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/)[1] : "");
    clean.starts_at = shortify(clean.starts_at);
    clean.ends_at = shortify(clean.ends_at);
    setDraft(clean);
  };

  return (
    <div className="space-y-8">
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-lg font-bold text-slate-900 mb-4">
          {draft.key && items.find((i) => i.key === draft.key) ? "Bearbeiten" : "Neue Kampagne anlegen"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Key (Slug)" value={draft.key} onChange={(v) => setDraft({ ...draft, key: v })} testid="campaign-form-key" placeholder="v102j_trial_push_modal" />
          <SelectField label="Ziel-Tier" value={draft.target_tier} onChange={(v) => setDraft({ ...draft, target_tier: v })} options={TIER_OPTIONS} testid="campaign-form-tier" />
          <TextField label="Headline" value={draft.headline} onChange={(v) => setDraft({ ...draft, headline: v })} testid="campaign-form-headline" full />
          <TextArea label="Body" value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })} testid="campaign-form-body" rows={5} />
          <TextField label="Primaerer CTA-Label" value={draft.cta_primary_label} onChange={(v) => setDraft({ ...draft, cta_primary_label: v })} testid="campaign-form-primary-label" />
          <TextField label="Primaerer CTA-URL" value={draft.cta_primary_url} onChange={(v) => setDraft({ ...draft, cta_primary_url: v })} testid="campaign-form-primary-url" placeholder="/pricing" />
          <TextField label="Sekundaerer CTA-Label" value={draft.cta_secondary_label} onChange={(v) => setDraft({ ...draft, cta_secondary_label: v })} testid="campaign-form-secondary-label" />
          <ToggleField label="Aktiv" value={draft.enabled} onChange={(v) => setDraft({ ...draft, enabled: v })} testid="campaign-form-enabled" />
          <TextField label="Startet (optional)" value={draft.starts_at} onChange={(v) => setDraft({ ...draft, starts_at: v })} testid="campaign-form-starts-at" type="datetime-local" />
          <TextField label="Endet (optional)" value={draft.ends_at} onChange={(v) => setDraft({ ...draft, ends_at: v })} testid="campaign-form-ends-at" type="datetime-local" />
        </div>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            data-testid="campaign-form-save"
            onClick={upsert}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </button>
          <button
            type="button"
            onClick={() => setDraft(BLANK_CAMPAIGN)}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
          >
            Zuruecksetzen
          </button>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-bold text-slate-900 mb-3">Vorhandene Kampagnen ({items.length})</h2>
        <div className="space-y-3">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Keine Kampagnen angelegt.</p>
          ) : (
            items.map((c) => (
              <div key={c.key} data-testid={`campaign-row-${c.key}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{c.key}</code>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${c.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                      {c.enabled ? "aktiv" : "inaktiv"}
                    </span>
                    <span className="text-xs text-slate-500">{c.target_tier}</span>
                  </div>
                  <p className="mt-1.5 font-semibold text-sm text-slate-900">{c.headline}</p>
                  <p className="text-xs text-slate-600 mt-1 line-clamp-3">{c.body}</p>
                  <p className="text-xs text-slate-500 mt-1">CTA: <span className="font-medium">{c.cta_primary_label}</span> → <code>{c.cta_primary_url}</code></p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button type="button" onClick={() => editExisting(c)} className="text-xs text-blue-700 hover:underline font-semibold">Bearbeiten</button>
                  <button type="button" onClick={() => resetDismisses(c.key)} className="text-xs text-slate-600 hover:underline">Dismisses reset</button>
                  <button type="button" onClick={() => remove(c.key)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Loeschen</button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form-Helpers
// ---------------------------------------------------------------------------
function TextField({ label, value, onChange, testid, placeholder, type = "text", full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, testid, rows = 4 }) {
  return (
    <div className="sm:col-span-2">
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={testid}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none font-mono"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ToggleField({ label, value, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="h-4 w-4 rounded text-amber-600 focus:ring-amber-500" />
      <span>{label}</span>
    </label>
  );
}
